/**
 * Stripe.js, obtained lazily and never at render time.
 *
 * <h2>The crash this exists to stop</h2>
 *
 * Both `Login` and `AuthDialog` called `window.Stripe(key)` in their component BODY — so on every
 * render, before any user had asked to pay anything. `index.html` loads `js.stripe.com/v3` with a
 * plain script tag, and when that request does not land — offline, a flaky network, a privacy
 * extension, a corporate proxy, a CSP — `window.Stripe` is undefined and the call throws
 * <em>during render</em>:
 *
 * <pre>
 *   TypeError: window.Stripe is not a function
 *       at Login (bundle.js:319562)
 * </pre>
 *
 * React cannot recover from a throw in render, so the whole login page white-screens. A customer
 * who cannot reach a third-party payment host is then unable to <b>log in</b> — and every one of
 * them is already a paying subscriber who needs nothing from Stripe to read a chart. The blast
 * radius was the entire product, for a dependency that matters on one button.
 *
 * <h2>Lazy, memoised, and it returns null instead of throwing</h2>
 *
 * Called from the checkout handler, which is the first moment Stripe is genuinely needed and a
 * moment where a failure has somewhere to be reported: both call sites already sit inside a
 * try/catch that puts `error.message` on screen.
 *
 * <p>Memoised because the old code built a fresh Stripe instance on every render of a form with a
 * dozen controlled inputs — one per keystroke.
 *
 * <p><b>Null rather than a throw</b>, so a caller has to decide what to do about it. A throw here
 * would be caught by the same handler and produce the same message, but it would also be one
 * refactor away from being uncaught again in a new call site.
 */

import appConfig from '../config/appConfig';

/**
 * The publishable key.
 *
 * <h2>Publishable keys are meant to ship in the bundle</h2>
 *
 * This is not a leaked secret and must not be moved into Secret Manager — it identifies the account
 * to Stripe's own API and is designed to be read by every visitor, exactly like the Firebase browser
 * key in `AuthContext`. The secret key lives on the backend in Secret Manager as
 * `stripe-secret-key`; nothing here can charge anyone.
 *
 * <h2>IT IS A TEST KEY, AND IT IS WHAT PRODUCTION SHIPS</h2>
 *
 * `pk_test_…`. It was hardcoded — identically — in two components, so production has been serving a
 * test-mode publishable key: a real customer reaching checkout gets Stripe's test environment,
 * which takes no money. That is a live-key configuration question rather than a bug in this file,
 * and it is now settable per deployment instead of needing a code change:
 * `REACT_APP_STRIPE_PUBLISHABLE_KEY`, alongside every other region override.
 */
const FALLBACK_PUBLISHABLE_KEY =
  'pk_test_51SQFWZLxxKRaIt4mjIDEsAwiGIqArNA4xVGyh9SELyoAUnDaXJ7i1Re0rpEvOmOuyHo0fhPyXabRdSftR3SSTz8F003uEhRjBV';

export function stripePublishableKey() {
  const configured = appConfig.stripePublishableKey;
  return typeof configured === 'string' && configured.trim() !== ''
    ? configured.trim()
    : FALLBACK_PUBLISHABLE_KEY;
}

/** Whether Stripe.js has actually loaded. Exported so a caller can ask before offering to pay. */
export function isStripeAvailable() {
  return typeof window !== 'undefined' && typeof window.Stripe === 'function';
}

/** The message a caller shows when it has not. Named here so both call sites say the same thing. */
export const STRIPE_UNAVAILABLE_MESSAGE =
  'Payment could not be started: the card processor did not load. '
  + 'Check your connection or any ad/privacy blocker, then try again.';

let cached = null;

/**
 * The Stripe instance, or null if Stripe.js is not there.
 *
 * <p>Note the name is not `stripePromise`, which is what the variable it replaces was called:
 * `window.Stripe()` is synchronous and returns the object, so the old `await stripePromise` was
 * awaiting a non-promise. Harmless, and misleading about where the failure could be.
 */
export function getStripe() {
  if (cached) return cached;
  if (!isStripeAvailable()) return null;
  try {
    cached = window.Stripe(stripePublishableKey());
    return cached;
  } catch (e) {
    // A malformed key throws here. Reported rather than swallowed, and still null to the caller so
    // one failure mode reaches the UI whatever caused it.
    console.error('[stripe] could not initialise:', e);
    return null;
  }
}

/** Test seam: forget the memoised instance. */
export function resetStripeForTests() {
  cached = null;
}

// ─── Whether a payment step exists at all ──────────────────────────────────────────────────────

/**
 * ⚠ THE FAIL-SAFE DIRECTION IS *BILLING ENABLED*, and it is the most important line in this file.
 *
 * A fetch can fail for a dozen reasons that have nothing to do with configuration — a flaky
 * network, an un-redeployed backend that has never heard of the endpoint, a proxy. If a failure
 * were read as "billing is off", every one of those would hand out a free paying account, silently,
 * and the only trace would be a cancelled request in a devtools panel nobody had open.
 *
 * <p>Read the other way the cost is a bad message and nothing more: a reader is asked for a card on
 * a deployment that is not charging, sees checkout fail, and reports it. Visible, recoverable, and
 * it takes no money.
 */
const BILLING_ENABLED_WHEN_UNKNOWN = true;

let billingEnabledCache = null;
let billingInFlight = null;

/**
 * Whether this deployment has a payment step — `GET /api/billing/config`, once per page load.
 *
 * <p>Memoised on the resolved value AND on the in-flight promise: `AuthDialog` and `Login` both ask,
 * and a signup form can ask twice in quick succession as it mounts, so without the second cache two
 * requests race and the loser's answer is thrown away.
 *
 * <p>The endpoint is PUBLIC, because the question is asked by a signup form before any account
 * exists — see `BillingConfigController`. That is also why this takes an `api` client rather than
 * reaching for an authenticated one: a 401 here would be read as "unknown" and default to enabled,
 * which is safe but would make the bypass unreachable for exactly the people it is for.
 *
 * @param {{get: Function}} api an axios-like client
 * @returns {Promise<boolean>} true when a card is required
 */
export async function isBillingEnabled(api) {
  if (billingEnabledCache !== null) return billingEnabledCache;
  if (billingInFlight) return billingInFlight;

  billingInFlight = (async () => {
    try {
      const { data } = await api.get('/api/billing/config');
      // An explicit false is the ONLY thing that disables billing. A missing field, a null, a
      // string, an HTML error page that happened to return 200 — all read as enabled.
      billingEnabledCache = data?.billingEnabled === false ? false : true;
    } catch (e) {
      console.warn('[billing] could not read /api/billing/config; assuming billing is enabled:', e);
      billingEnabledCache = BILLING_ENABLED_WHEN_UNKNOWN;
    } finally {
      billingInFlight = null;
    }
    return billingEnabledCache;
  })();

  return billingInFlight;
}

/** The line shown in place of a price selector when there is nothing to pay. */
export const BILLING_DISABLED_NOTICE =
  'No payment is required at the moment — your account is activated immediately.';

/** Test seam: forget the cached answer and any in-flight request. */
export function resetBillingConfigForTests() {
  billingEnabledCache = null;
  billingInFlight = null;
}
